import React, {
  memo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import './file-upload-failure.scss';
import { File, Trash2 } from 'react-feather';
import FeaturedIcon from '../../../featured-icon/featured-icon';
import { IconColors, IconSizes } from '../../../icon';
import Button, { ButtonSizes, ButtonTypes } from '../../../button/button';
import Icon from '../../../icon/icon';
import useTranslation from '../../../../../utils/translation-utils';
import Typography from '../../../typography';
import { getErrorMessageByCode } from '../../utils';

const FileUploadFailure = ({
  maxSize, fileErrors, fileName, onDelete, apiErrors, allFilesUploaded, setErrorMessages,
}) => {
  const [getTranslation] = useTranslation();
  const hasFileUploadError = fileErrors.length > 0;
  const hasApiError = apiErrors.length > 0;
  const showErrorMessage = hasApiError || hasFileUploadError;

  const handleOnDelete = useCallback(() => {
    if (allFilesUploaded && hasApiError) {
      setErrorMessages([]);
    }
    onDelete();
  }, [onDelete, allFilesUploaded, hasApiError]);

  const renderFileIcon = useCallback(({ size, color }) => (
    <Icon
      size={size}
      icon={File}
      color={color}
    />
  ), []);

  const renderTrashIcon = useCallback(({ size, color }) => (
    <Icon
      size={size}
      color={color}
      icon={Trash2}
    />
  ), []);

  const getErrorMessage = (id, message) => (
    <Typography
      key={id}
      className="err-msg"
      variant={Typography.Variants.xs}
      color={Typography.Colors.gray500}
      fontWeight={Typography.FontWeight.regular}
    >
      {message}
    </Typography>
  );

  const getErrorMessages = () => {
    const messages = [];

    if (hasApiError) {
      apiErrors.forEach((message, index) => {
        messages.push(getErrorMessage(`api-error${index}`, message));
      });
    }

    if (hasFileUploadError) {
      fileErrors.forEach((error) => {
        const message = getErrorMessageByCode(getTranslation, error, maxSize);
        messages.push(getErrorMessage(error.code, message));
      });
    }

    return (
      <div className="ufm-error-messages">
        {messages}
      </div>
    );
  };

  return showErrorMessage && (
    <div className="upload-failure-cont">
      <FeaturedIcon renderIcon={renderFileIcon} color={IconColors.ERROR} size={IconSizes.SMALL} />
      <div className="upload-failure-msgs">
        <Typography
          className="file-name"
          variant={Typography.Variants.sm}
          color={Typography.Colors.gray700}
          fontWeight={Typography.FontWeight.medium}
        >
          {fileName}
        </Typography>
        {showErrorMessage && getErrorMessages()}
      </div>
      <div className="ufc-actions">
        <Button
          onClick={handleOnDelete}
          size={ButtonSizes.X_SMALL}
          renderIcon={renderTrashIcon}
          type={ButtonTypes.LINK_DESTRUCTIVE}
        />
      </div>
    </div>
  );
};

FileUploadFailure.defaultProps = {
  maxSize: 0,
  fileName: '',
  apiErrors: [],
  fileErrors: [],
  onDelete: () => {},
  isDialogOpen: false,
  allFilesUploaded: false,
  setErrorMessages: () => {},
};

FileUploadFailure.propTypes = {
  onDelete: PropTypes.func,
  maxSize: PropTypes.number,
  fileName: PropTypes.string,
  isDialogOpen: PropTypes.bool,
  allFilesUploaded: PropTypes.bool,
  setErrorMessages: PropTypes.func,
  apiErrors: PropTypes.arrayOf(PropTypes.string),
  fileErrors: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
  })),
};

export default memo(FileUploadFailure);
